// /src/app/modules/cuentas/components/CuentaInfoCard.tsx
import React from 'react'

type CuentaInfo = {
  numero: number
  activa: number
  idMiembro: number
  categoria: number
  zonaCobro: number | null
  exentaIva: number
  exentaIrae: number
  categoriaNombre: string
  nombreFantasia?: string
  razonSocial?: string
  socio?: number
}

interface CuentaInfoCardProps {
  cuenta: CuentaInfo
  onEditar: () => void
}

const CuentaInfoCard: React.FC<CuentaInfoCardProps> = ({cuenta, onEditar}) => {
  return (
    <div className='card'>
      <div className='card-header border-0 pt-6'>
        <div className='card-title'>
          <i className='ki-duotone ki-wallet fs-2 me-2 text-primary'>
            <span className='path1'></span>
            <span className='path2'></span>
            <span className='path3'></span>
            <span className='path4'></span>
          </i>
          <h3 className='fw-bold m-0'>Información de la Cuenta</h3>
        </div>
        <div className='card-toolbar'>
          <span className={`badge badge-light-${cuenta.activa === 1 ? 'success' : 'danger'} fs-5 px-4 py-2`}>
            {cuenta.activa === 1 ? (
              <>
                <i className='ki-duotone ki-check-circle fs-3 me-1'>
                  <span className='path1'></span>
                  <span className='path2'></span>
                </i>
                Cuenta Activa
              </>
            ) : (
              <>
                <i className='ki-duotone ki-cross-circle fs-3 me-1'>
                  <span className='path1'></span>
                  <span className='path2'></span>
                </i>
                Cuenta Inactiva
              </>
            )}
          </span>
        </div>
      </div>
      <div className='card-body pt-0'>
        <div className='row g-6'>
          {/* Columna izquierda */}
          <div className='col-lg-6'>
            <div className='d-flex flex-column gap-5'>
              <div className='d-flex align-items-start'>
                <div className='symbol symbol-40px me-4'>
                  <div className='symbol-label bg-light-primary'>
                    <i className='ki-duotone ki-abstract-26 fs-2 text-primary'>
                      <span className='path1'></span>
                      <span className='path2'></span>
                    </i>
                  </div>
                </div>
                <div className='flex-grow-1'>
                  <span className='text-muted fw-semibold d-block fs-7'>Número de Cuenta</span>
                  <span className='text-gray-800 fw-bold fs-3'>#{cuenta.numero}</span>
                </div>
              </div>

              {cuenta.socio && (
                <div className='d-flex align-items-start'>
                  <div className='symbol symbol-40px me-4'>
                    <div className='symbol-label bg-light-info'>
                      <i className='ki-duotone ki-badge fs-2 text-info'>
                        <span className='path1'></span>
                        <span className='path2'></span>
                        <span className='path3'></span>
                        <span className='path4'></span>
                        <span className='path5'></span>
                      </i>
                    </div>
                  </div>
                  <div className='flex-grow-1'>
                    <span className='text-muted fw-semibold d-block fs-7'>Número de Socio</span>
                    <span className='text-gray-800 fw-bold fs-5'>{cuenta.socio}</span>
                  </div>
                </div>
              )}

              {cuenta.nombreFantasia && (
                <div className='d-flex align-items-start'>
                  <div className='symbol symbol-40px me-4'>
                    <div className='symbol-label bg-light-success'>
                      <i className='ki-duotone ki-profile-circle fs-2 text-success'>
                        <span className='path1'></span>
                        <span className='path2'></span>
                        <span className='path3'></span>
                      </i>
                    </div>
                  </div>
                  <div className='flex-grow-1'>
                    <span className='text-muted fw-semibold d-block fs-7'>Nombre Fantasía</span>
                    <span className='text-gray-800 fw-bold fs-6'>{cuenta.nombreFantasia}</span>
                  </div>
                </div>
              )}

              {cuenta.razonSocial && (
                <div className='d-flex align-items-start'>
                  <div className='symbol symbol-40px me-4'>
                    <div className='symbol-label bg-light-warning'>
                      <i className='ki-duotone ki-office-bag fs-2 text-warning'>
                        <span className='path1'></span>
                        <span className='path2'></span>
                        <span className='path3'></span>
                        <span className='path4'></span>
                      </i>
                    </div>
                  </div>
                  <div className='flex-grow-1'>
                    <span className='text-muted fw-semibold d-block fs-7'>Razón Social</span>
                    <span className='text-gray-800 fw-bold fs-6'>{cuenta.razonSocial}</span>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Columna derecha */}
          <div className='col-lg-6'>
            <div className='d-flex flex-column gap-5'>
              <div className='d-flex align-items-start'>
                <div className='symbol symbol-40px me-4'>
                  <div className='symbol-label bg-light-primary'>
                    <i className='ki-duotone ki-category fs-2 text-primary'>
                      <span className='path1'></span>
                      <span className='path2'></span>
                      <span className='path3'></span>
                      <span className='path4'></span>
                    </i>
                  </div>
                </div>
                <div className='flex-grow-1'>
                  <span className='text-muted fw-semibold d-block fs-7'>Categoría</span>
                  <span className='badge badge-primary fs-6 mt-1'>{cuenta.categoriaNombre}</span>
                </div>
              </div>

              <div className='d-flex align-items-start'>
                <div className='symbol symbol-40px me-4'>
                  <div className='symbol-label bg-light-info'>
                    <i className='ki-duotone ki-geolocation fs-2 text-info'>
                      <span className='path1'></span>
                      <span className='path2'></span>
                    </i>
                  </div>
                </div>
                <div className='flex-grow-1'>
                  <span className='text-muted fw-semibold d-block fs-7'>Zona de Cobro</span>
                  <span className='text-gray-800 fw-bold fs-6'>{cuenta.zonaCobro || 'No asignada'}</span>
                </div>
              </div>

              <div className='d-flex align-items-start'>
                <div className='symbol symbol-40px me-4'>
                  <div className='symbol-label bg-light-warning'>
                    <i className='ki-duotone ki-percentage fs-2 text-warning'>
                      <span className='path1'></span>
                      <span className='path2'></span>
                    </i>
                  </div>
                </div>
                <div className='flex-grow-1'>
                  <span className='text-muted fw-semibold d-block fs-7 mb-2'>Exenciones</span>
                  <div className='d-flex gap-2'>
                    <span className={`badge badge-light-${cuenta.exentaIva === 1 ? 'success' : 'secondary'}`}>
                      IVA: {cuenta.exentaIva === 1 ? 'Exenta' : 'No exenta'}
                    </span>
                    <span className={`badge badge-light-${cuenta.exentaIrae === 1 ? 'success' : 'secondary'}`}>
                      IRAE: {cuenta.exentaIrae === 1 ? 'Exenta' : 'No exenta'}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default CuentaInfoCard

